import { computed, type Ref } from 'vue';
import type { FormElement } from '../model/form.types';
import { CANVAS_LAYOUT } from '../lib/canvas-constants';

interface ElementPosition {
  accumulatedTop: number;
  marginTop: number;
}

interface PageBreak {
  top: number;
  pageNumber: number;
}

export function useCanvasPagination(
  elements: Ref<FormElement[]>,
  elementRefs: Ref<Map<string, HTMLElement>>,
  hasElements: Ref<boolean>,
  orientation: Ref<'portrait' | 'landscape'>,
  updateTrigger?: Ref<number>,
  dynamicPageHeight?: Ref<number>,
  headerHeightRef?: Ref<number>
) {
  const PAGE_HEIGHT = computed(() => {
    if (dynamicPageHeight) return dynamicPageHeight.value;
    return orientation.value === 'portrait' ? CANVAS_LAYOUT.PAGE_HEIGHT : CANVAS_LAYOUT.PAGE_HEIGHT_LANDSCAPE;
  });
  const CANVAS_PADDING: number = CANVAS_LAYOUT.CANVAS_PADDING;
  const BOTTOM_MARGIN: number = CANVAS_LAYOUT.BOTTOM_MARGIN;
  const TOP_MARGIN: number = CANVAS_LAYOUT.TOP_MARGIN;
  const HEADER_TO_CONTENT_GAP: number = CANVAS_LAYOUT.HEADER_TO_CONTENT_GAP;

  const elementPositions = computed(() => {
    if (updateTrigger) {
      void updateTrigger.value;
    }
    const headerHeight = headerHeightRef?.value ?? 0;

    const positions = new Map<string, ElementPosition>();

    const wrapperHeights: number[] = [];
    elements.value.forEach((element) => {
      if (!element) {
        wrapperHeights.push(CANVAS_LAYOUT.DEFAULT_ELEMENT_HEIGHT);
        return;
      }
      const elementRef = elementRefs.value.get(element.id);
      const wrapper = elementRef?.parentElement;
      const height = wrapper?.offsetHeight || elementRef?.offsetHeight || CANVAS_LAYOUT.DEFAULT_ELEMENT_HEIGHT;
      wrapperHeights.push(height);
    });

    let currentTop = CANVAS_PADDING;

    elements.value.forEach((element, index) => {
      if (!element) return;

      const wrapperHeight = wrapperHeights[index];
      const elementTopInCanvas = currentTop;
      const elementBottomInCanvas = elementTopInCanvas + wrapperHeight;

      const currentPage = Math.floor(elementTopInCanvas / PAGE_HEIGHT.value);
      const nextPageBreakY = (currentPage + 1) * PAGE_HEIGHT.value;
      const safeZoneEnd = nextPageBreakY - BOTTOM_MARGIN;

      let marginTop = 0;

      const crossesPageBreak = elementBottomInCanvas > nextPageBreakY;
      const tooCloseToBreak = elementBottomInCanvas > safeZoneEnd && elementTopInCanvas < nextPageBreakY;

      const maxElementHeight = PAGE_HEIGHT.value - TOP_MARGIN - BOTTOM_MARGIN;
      const canFitOnPage = wrapperHeight <= maxElementHeight;

      if ((crossesPageBreak || tooCloseToBreak) && canFitOnPage) {
        const offsetBelowPageTop =
          headerHeight > 0
            ? CANVAS_PADDING + headerHeight + (element.type === 'TABLE' ? HEADER_TO_CONTENT_GAP : 0)
            : TOP_MARGIN;
        const targetTopInCanvas = nextPageBreakY + offsetBelowPageTop;
        marginTop = targetTopInCanvas - currentTop;
      }

      const finalTop = currentTop + marginTop;
      const finalBottom = finalTop + wrapperHeight;

      positions.set(element.id, {
        accumulatedTop: finalTop,
        marginTop,
      });

      currentTop = finalBottom;
    });

    return positions;
  });

  const totalContentHeight = computed(() => {
    if (updateTrigger) {
      void updateTrigger.value;
    }

    const lastElement = elements.value[elements.value.length - 1];
    if (!lastElement) return CANVAS_PADDING;

    const lastPosition = elementPositions.value.get(lastElement.id);
    const lastHeight =
      elementRefs.value.get(lastElement.id)?.offsetHeight || CANVAS_LAYOUT.DEFAULT_ELEMENT_HEIGHT;

    if (!lastPosition) return CANVAS_PADDING;

    return lastPosition.accumulatedTop + lastHeight;
  });

  const pageCount = computed(() => {
    if (!hasElements.value) {
      return 1;
    }
    return Math.max(1, Math.ceil(totalContentHeight.value / PAGE_HEIGHT.value));
  });

  const canvasHeight = computed(() => {
    return `${PAGE_HEIGHT.value * pageCount.value}px`;
  });

  const pageBreakPositions = computed((): PageBreak[] => {
    const positions: PageBreak[] = [];
    for (let i = 1; i < pageCount.value; i++) {
      positions.push({
        top: i * PAGE_HEIGHT.value,
        pageNumber: i + 1,
      });
    }
    return positions;
  });

  const getElementMarginTop = (elementId: string): string => {
    const position = elementPositions.value.get(elementId);
    return position && position.marginTop > 0 ? `${position.marginTop}px` : '0px';
  };

  return {
    elementPositions,
    totalContentHeight,
    pageCount,
    canvasHeight,
    pageBreakPositions,
    getElementMarginTop,
  };
}
